// @ts-expect-error
import * as csstree from 'css-tree'

enum CssSyntax {
    Property = 'property',
    Type = 'type'
}

const extendedCsstree = csstree.fork({
    units: {
        length: [...csstree.lexer.units.length, 'spx']
    }
})

const matchSyntax = (syntax: CssSyntax, name: string, value: csstree.CssNode): csstree.LexerMatchResult => {
    switch (syntax) {
        case CssSyntax.Property:
            return extendedCsstree.lexer.matchProperty(name, value)
        case CssSyntax.Type:
            return extendedCsstree.lexer.matchType(name, value)
    }
}

const createCSSFormatValidator = (cssSyntaxName: string, syntax: CssSyntax) => (val: string) => {
    try {
        const ast = extendedCsstree.parse(val, {context: 'value'})
        const matchResult = matchSyntax(syntax, cssSyntaxName, ast)
        return matchResult.error === null
    } catch {
        return false
    }
}

const cssFormats: Record<string, (value: string) => boolean> = {
    'css-color': createCSSFormatValidator('color', CssSyntax.Type),
    'css-blend-mode': createCSSFormatValidator('blend-mode', CssSyntax.Type),
    'css-angle': createCSSFormatValidator('angle', CssSyntax.Type),
    'css-length-percentage': createCSSFormatValidator('length-percentage', CssSyntax.Type),
    'css-length': createCSSFormatValidator('length', CssSyntax.Type),
    'css-number': createCSSFormatValidator('number', CssSyntax.Type),
    'css-percentage': createCSSFormatValidator('percentage', CssSyntax.Type),
    'css-string': createCSSFormatValidator('string', CssSyntax.Type),
    'css-time': createCSSFormatValidator('time', CssSyntax.Type),

    'css-border-top-style': createCSSFormatValidator('border-top-style', CssSyntax.Property),
    'css-border-top-width': createCSSFormatValidator('border-top-width', CssSyntax.Property),
    'css-border-width': createCSSFormatValidator('border-width', CssSyntax.Property),
    'css-border-style': createCSSFormatValidator('border-style', CssSyntax.Property),
    'css-border-color': createCSSFormatValidator('border-color', CssSyntax.Property),
    'css-border': createCSSFormatValidator('border', CssSyntax.Property),
    'css-border-top-left-radius': createCSSFormatValidator('border-top-left-radius', CssSyntax.Property),
    'css-border-radius': createCSSFormatValidator('border-radius', CssSyntax.Property),
    'css-font': createCSSFormatValidator('font', CssSyntax.Property),
    'css-font-family': createCSSFormatValidator('font-family', CssSyntax.Property),
    'css-font-size': createCSSFormatValidator('font-size', CssSyntax.Property),
    'css-font-stretch': createCSSFormatValidator('font-stretch', CssSyntax.Property),
    'css-font-style': createCSSFormatValidator('font-style', CssSyntax.Property),
    'css-font-variant': createCSSFormatValidator('font-variant', CssSyntax.Property),
    'css-font-weight': createCSSFormatValidator('font-weight', CssSyntax.Property),
    'css-line-height': createCSSFormatValidator('line-height', CssSyntax.Property),
    'css-letter-spacing': createCSSFormatValidator('letter-spacing', CssSyntax.Property),
    'css-text-align': createCSSFormatValidator('text-align', CssSyntax.Property),
    'css-text-transform': createCSSFormatValidator('text-transform', CssSyntax.Property),
    'css-text-shadow': createCSSFormatValidator('text-shadow', CssSyntax.Property),
    'css-text-decoration': createCSSFormatValidator('text-decoration', CssSyntax.Property),
    'css-text-decoration-line': createCSSFormatValidator('text-decoration-line', CssSyntax.Property),
    'css-text-decoration-style': createCSSFormatValidator('text-decoration-style', CssSyntax.Property),
    'css-text-decoration-thickness': createCSSFormatValidator('text-decoration-thickness', CssSyntax.Property),
    'css-text-overflow': createCSSFormatValidator('text-overflow', CssSyntax.Property),
    'css-text-indent': createCSSFormatValidator('text-indent', CssSyntax.Property),
    'css-padding': createCSSFormatValidator('padding', CssSyntax.Property),
    'css-padding-top': createCSSFormatValidator('padding-top', CssSyntax.Property),
    'css-margin': createCSSFormatValidator('margin', CssSyntax.Property),
    'css-margin-top': createCSSFormatValidator('margin-top', CssSyntax.Property),
    'css-box-shadow': createCSSFormatValidator('box-shadow', CssSyntax.Property),
    'css-opacity': createCSSFormatValidator('opacity', CssSyntax.Property),
    'css-background': createCSSFormatValidator('background', CssSyntax.Property),
    'css-background-size': createCSSFormatValidator('background-size', CssSyntax.Property),
    'css-background-image': createCSSFormatValidator('background-image', CssSyntax.Property),
    'css-align-self': createCSSFormatValidator('align-self', CssSyntax.Property),
    'css-height-or-width': createCSSFormatValidator('height', CssSyntax.Property),
    'css-background-clip': createCSSFormatValidator('background-clip', CssSyntax.Property),
    'css-background-origin': createCSSFormatValidator('background-origin', CssSyntax.Property),
    'css-background-position': createCSSFormatValidator('background-position', CssSyntax.Property),
    'css-background-repeat': createCSSFormatValidator('background-repeat', CssSyntax.Property),
    'css-background-attachment': createCSSFormatValidator('background-attachment', CssSyntax.Property),
    'css-overflow': createCSSFormatValidator('overflow', CssSyntax.Property),
    'css-overflow-x': createCSSFormatValidator('overflow-x', CssSyntax.Property),
    'css-overflow-y': createCSSFormatValidator('overflow-y', CssSyntax.Property),
    'css-align-items': createCSSFormatValidator('align-items', CssSyntax.Property),
    'css-flex-direction': createCSSFormatValidator('flex-direction', CssSyntax.Property),
    'css-gap': createCSSFormatValidator('gap', CssSyntax.Property),
    'css-single-value-gap': createCSSFormatValidator('column-gap', CssSyntax.Property),
    'css-justify-content': createCSSFormatValidator('justify-content', CssSyntax.Property),
    'css-fill-with-url': createCSSFormatValidator('fill', CssSyntax.Property),
    'css-isolation': createCSSFormatValidator('isolation', CssSyntax.Property),
    'css-mix-blend-mode': createCSSFormatValidator('mix-blend-mode', CssSyntax.Property),
    'css-object-fit': createCSSFormatValidator('object-fit', CssSyntax.Property),
    'css-object-position': createCSSFormatValidator('object-position', CssSyntax.Property),
    'css-fill-opacity': createCSSFormatValidator('opacity', CssSyntax.Property), // opacity and fill-opacity have the same definition on MDN, lexer has a bug for fill-opacity
    'css-stroke': createCSSFormatValidator('stroke', CssSyntax.Property),
    'css-stroke-width': createCSSFormatValidator('stroke-width', CssSyntax.Property),
    'css-stroke-opacity': createCSSFormatValidator('stroke-opacity', CssSyntax.Property),
    'css-display': createCSSFormatValidator('display', CssSyntax.Property),
    'css-filter': createCSSFormatValidator('filter', CssSyntax.Property),
    'css-writing-mode': createCSSFormatValidator('writing-mode', CssSyntax.Property)
}

export const getCssFormats = () => cssFormats
